import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { DateIntervalFilterData } from '../../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { PartInventoryItem } from '../../../models/inventory-overview.model';
import { PartDetails } from '../../../models/part-details.model';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';
import { CompatiblePartsTabComponent } from './compatible-parts-tab/compatible-parts-tab.component';
import { PartDetailsTabComponent } from './part-details-tab/part-details-tab.component';
import { PartUsageTabComponent } from './part-usage-tab/part-usage-tab.component';
import { ReceivedPartsTabComponent } from './received-parts-tab/received-parts-tab.component';

const PART_OVERVIEW_TABS = ['part-details', 'received-parts', 'part-usage', 'compatible-parts'] as const;
type PartOverviewTab = typeof PART_OVERVIEW_TABS[number];

@Component({
  selector: 'app-part-overview',
  standalone: true,
  imports: [
    CommonModule,
    PartDetailsTabComponent,
    ReceivedPartsTabComponent,
    PartUsageTabComponent,
    CompatiblePartsTabComponent,
    TranslocoDirective
  ],
  templateUrl: './part-overview.component.html',
  styleUrl: './part-overview.component.scss'
})
export class PartOverviewComponent implements OnInit {
  @Input() partId!: number;
  @Input() accountIds: number[] = [];
  @Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  @Input() inventoryItem: PartInventoryItem | null = null;

  readonly tabs = PART_OVERVIEW_TABS;
  activeTab: PartOverviewTab = 'part-details';
  partDetails: PartDetails | null = null;
  isPartLoading = false;
  private hasStockChanged = false;

  private readonly activeModal = inject(NgbActiveModal);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly partsAndInventoryService = inject(PartsAndInventoryService);
  private readonly transloco = inject(TranslocoService);

  ngOnInit(): void {
    void this.loadPartDetailsAsync();
  }

  get modalTitle(): string {
    const partName: string =
      this.partDetails?.partName ??
      this.inventoryItem?.partName ??
      this.transloco.translate<string>('parts.modals.partOverview.fallbackTitle');
    const partNumber = this.partDetails?.partNumber ?? this.inventoryItem?.partNumber ?? '';
    return partNumber ? `${partName} - ${partNumber}` : partName;
  }

  setActiveTab(tab: PartOverviewTab): void {
    this.activeTab = tab;
  }

  onDetailsChanged(): void {
    void this.loadPartDetailsAsync();
  }

  onStockAdded(): void {
    this.hasStockChanged = true;
  }

  close(): void {
    if (this.hasStockChanged) {
      this.activeModal.close(true);
    } else {
      this.activeModal.dismiss();
    }
  }

  private async loadPartDetailsAsync(): Promise<void> {
    this.isPartLoading = true;

    try {
      const response = await this.partsAndInventoryService.getPartByIdAsync(this.partId);
      this.partDetails = response.data ?? null;
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isPartLoading = false;
    }
  }
}